import { useState, useEffect } from 'react';
import { Heart, Loader2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [5, 10, 15, 20, 25, 50];

/**
 * Buyer-side tip CTA shown on completed/in-progress bookings.
 * Vendor keeps 100% — platform takes nothing.
 */
export default function TipButton({ booking, vendorName, onTipSent }) {
  const { getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const [selected, setSelected] = useState(15);
  const [loading, setLoading] = useState(false);

  // Auto-confirm on redirect back from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tip = params.get('tip');
    const sessionId = params.get('session_id');
    if (tip === 'success' && sessionId) {
      (async () => {
        try {
          const res = await fetch(`${API}/tips/confirm/${sessionId}`, {
            method: 'POST',
            headers: getAuthHeaders(),
          });
          if (res.ok) {
            const d = await res.json();
            if (d.status === 'paid') {
              toast.success(`💚 Thanks for tipping $${d.amount}! Vendors keep 100%.`);
              onTipSent?.();
              window.history.replaceState(null, '', window.location.pathname);
            }
          }
        } catch {}
      })();
    }
  }, []);

  const startCheckout = async () => {
    const amount = custom ? Number(custom) : selected;
    if (!amount || amount < 1) return toast.error('Tip must be at least $1');
    if (amount > 500) return toast.error('Tips are capped at $500');
    setLoading(true);
    try {
      const res = await fetch(`${API}/tips/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          booking_id: booking.booking_id,
          amount,
          origin: window.location.origin,
        }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data?.detail || 'Could not start tip checkout');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const alreadyTipped = (booking.tip_total || 0) > 0;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#F472B6] bg-[#F472B6]/10 border border-[#F472B6]/30 hover:bg-[#F472B6]/20 transition"
        data-testid={`tip-button-${booking.booking_id}`}
      >
        <Heart size={12} fill="currentColor" /> {alreadyTipped ? `Tipped $${booking.tip_total} · Tip again` : 'Leave a tip'}
      </button>
    );
  }

  return (
    <div className="mt-3 p-4 rounded-xl bg-gradient-to-br from-[#F472B6]/10 to-[#EC4899]/5 border border-[#F472B6]/30 relative" data-testid={`tip-panel-${booking.booking_id}`}>
      <button onClick={() => setOpen(false)} className="absolute top-2 right-2 text-[#9CA3AF] hover:text-white p-1">
        <X size={14} />
      </button>

      <div className="flex items-center gap-2 mb-2">
        <Heart size={14} className="text-[#F472B6]" fill="currentColor" />
        <p className="text-sm font-semibold text-white">Tip {vendorName || 'your vendor'}</p>
      </div>
      <p className="text-[11px] text-[#9CA3AF] mb-4">
        100% goes to the vendor. EntrepreNatives takes ZERO platform fee on tips.
      </p>

      <div className="grid grid-cols-6 gap-1.5 mb-3">
        {PRESETS.map((v) => (
          <button
            key={v}
            onClick={() => { setSelected(v); setCustom(''); }}
            className={`py-2 rounded-lg text-xs font-bold transition ${selected === v && !custom ? 'bg-[#F472B6] text-white' : 'bg-white/5 text-[#9CA3AF] hover:bg-white/10'}`}
            data-testid={`tip-preset-${v}`}
          >
            ${v}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-[#64748B] text-xs">or custom:</span>
        <div className="relative flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">$</span>
          <input
            type="number"
            min="1"
            max="500"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Other"
            className="w-full pl-6 pr-2 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-[#64748B]"
            data-testid="tip-custom-input"
          />
        </div>
      </div>

      <button
        onClick={startCheckout}
        disabled={loading}
        className="w-full py-2.5 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-[#F472B6] to-[#EC4899] disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="tip-send-btn"
      >
        {loading ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : <><Check size={14} /> Send ${custom || selected} tip</>}
      </button>
    </div>
  );
}
